import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import IncompleteCircleIcon from '@mui/icons-material/IncompleteCircle';

/** Mirrors `PROMPT_TASK_VERSION` in `analysis/prompt_tasks.ts`. */
const BRIEF_TASK = 'brief_v21';

/**
 * The ten coding-key nodes in display order, grouped as in the protocol.
 * Mirrors `BRIEF_NODES` in `analysis/prompt_tasks.ts`; labels are `rmet.node_<key>`.
 */
const BRIEF_NODE_GROUPS: { group: string; keys: string[] }[] = [
  {
    group: 'team_context',
    keys: [
      'size_composition',
      'purpose',
      'division_of_work',
      'rhythm_and_material',
      'current_situation',
    ],
  },
  {
    group: 'agent_specification',
    keys: ['named_signals', 'group_norm', 'output_shape', 'recipient'],
  },
  { group: 'boundary', keys: ['surveillance_constraint'] },
];

const BRIEF_NODE_COUNT = BRIEF_NODE_GROUPS.reduce((n, g) => n + g.keys.length, 0);

/**
 * Scorer 3.x stores a 0/1/2 level per node plus the fields marked "3.x";
 * rows still coded by 2.x store a boolean per node and lack them.
 */
export interface BriefTaskScores {
  task: typeof BRIEF_TASK;
  teamType: string | null;
  nodes: Record<string, boolean | number>;
  notes: Record<string, string>;
  total: number;
  maxScore: number;
  scoredAt: string;
  scorerVersion: string;
  error?: string;
  /** 3.x */
  validity?: 'on_task' | 'off_task' | 'no_attempt' | null;
  /** 3.x */
  individualMonitoring?: boolean;
  /** 3.x — nodes at level 2. */
  strictTotal?: number;
  /** 3.x */
  wordCount?: number;
}

/** A node's value on the 0/1/2 scale; a 2.x pass is a full level 2. */
const nodeLevel = (v: boolean | number | undefined): number =>
  v === true ? 2 : typeof v === 'number' ? v : 0;

/** One task of the retired three-task format, scored before the brief task. */
export interface TaskResult {
  prompt: string;
  output: string;
  score: number;
  maxScore: number;
  detail: Record<string, boolean>;
  notes?: Record<string, string>;
  error?: string;
}

export interface LegacyPromptTaskScores {
  task1: TaskResult;
  task2: TaskResult;
  task3: TaskResult;
  total: number;
  scoredAt: string;
  scorerVersion: string;
}

export type PromptTaskScores = BriefTaskScores | LegacyPromptTaskScores;

const LEGACY_MAX_SCORE = 14;

const isLegacyScores = (s: PromptTaskScores): s is LegacyPromptTaskScores => 'task1' in s;

/** "Brief 7 / 10", or "Prompts 9 / 14" for a legacy row. */
export const promptScoreLabel = (s: PromptTaskScores, t: (key: string) => string): string =>
  isLegacyScores(s)
    ? `${t('rmet.detail_prompt_total')} ${s.total} / ${LEGACY_MAX_SCORE}`
    : `${t('rmet.detail_brief_total')} ${s.total} / ${s.maxScore}`;

export interface RmetSubmission {
  id: string;
  sessionId: string;
  sessionLabel: string;
  submittedAt: string;
  demographics: Record<string, string>;
  score: number;
  /** `[brief]` on current rows; the three task prompts on legacy rows. */
  prompts: string[];
  nomination?: string;
  /** Absent on rows from the retired three-task format. */
  promptTask?: typeof BRIEF_TASK;
  lang: string;
  timings?: {
    rmetStartedAt?: string;
    rmetEndedAt?: string;
    promptsStartedAt?: string;
    taskStartedAt?: string;
    promptsEndedAt?: string;
  };
  promptScores?: PromptTaskScores;
  promptScoreStatus?: 'pending' | 'done' | 'failed';
}

const RMET_TOTAL = 36;

/** "4:07" from two ISO timestamps, or null when either is missing. */
export const durationBetween = (from?: string, to?: string): string | null => {
  if (!from || !to) return null;
  const ms = Date.parse(to) - Date.parse(from);
  if (!Number.isFinite(ms) || ms < 0) return null;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

/** Monospace block for prompts and model output, scrolling inside its own box. */
const TextBlock: React.FC<{ text: string; muted?: boolean }> = ({ text, muted }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      bgcolor: 'action.hover',
      maxHeight: 280,
      overflow: 'auto',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      fontFamily: 'monospace',
      fontSize: 13,
      color: muted ? 'text.secondary' : 'text.primary',
    }}
  >
    {text}
  </Paper>
);

/** One pass/fail line with the coder's evidence underneath. */
const CriterionRow: React.FC<{
  label: string;
  passed: boolean;
  note?: string;
  monospace?: boolean;
}> = ({ label, passed, note, monospace }) => (
  <Box>
    <Box display="flex" alignItems="center" gap={0.75}>
      {passed ? (
        <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
      ) : (
        <CancelIcon color="disabled" sx={{ fontSize: 16 }} />
      )}
      <Typography
        variant="body2"
        sx={monospace ? { fontFamily: 'monospace', fontSize: 12 } : undefined}
        color={passed ? 'text.primary' : 'text.secondary'}
      >
        {label}
      </Typography>
    </Box>
    {note && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ pl: 3, display: 'block', fontStyle: 'italic' }}
      >
        {note}
      </Typography>
    )}
  </Box>
);

/** A task of the retired three-task format: prompt, model output, criteria. */
const TaskPanel: React.FC<{ n: number; task?: TaskResult; promptFallback: string }> = ({
  n,
  task,
  promptFallback,
}) => {
  const { t } = useTranslation();
  // Before scoring runs we still have the participant's prompt, just no output.
  const prompt = task?.prompt || promptFallback;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {t('rmet.task_label', { n })} — {t(`rmet.task_${n}_title`)}
        </Typography>
        {task && (
          <Chip
            size="small"
            color={
              task.score === task.maxScore ? 'success' : task.score > 0 ? 'warning' : 'default'
            }
            label={`${task.score} / ${task.maxScore}`}
          />
        )}
      </Box>

      {task?.error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {task.error}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary">
        {t('rmet.detail_prompt')}
      </Typography>
      <TextBlock text={prompt || '—'} muted={!prompt} />

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        {t('rmet.detail_output')}
      </Typography>
      <TextBlock text={task?.output || t('rmet.detail_not_scored')} muted={!task?.output} />

      {task && Object.keys(task.detail).length > 0 && (
        <Box mt={1.5}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            {t('rmet.detail_criteria')}
          </Typography>
          <Box display="flex" flexDirection="column" gap={0.25}>
            {Object.entries(task.detail).map(([key, passed]) => (
              <CriterionRow
                key={key}
                label={key}
                monospace
                passed={passed}
                note={task.notes?.[key]}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

/** One coding-key node: its level out of 2, with the coder's evidence underneath. */
const NodeRow: React.FC<{ label: string; level: number; note?: string }> = ({
  label,
  level,
  note,
}) => (
  <Box>
    <Box display="flex" alignItems="center" gap={0.75}>
      {level === 2 ? (
        <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
      ) : level === 1 ? (
        <IncompleteCircleIcon color="warning" sx={{ fontSize: 16 }} />
      ) : (
        <CancelIcon color="disabled" sx={{ fontSize: 16 }} />
      )}
      <Typography
        variant="body2"
        color={level ? 'text.primary' : 'text.secondary'}
        sx={{ flex: 1 }}
      >
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {level} / 2
      </Typography>
    </Box>
    {note && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ pl: 3, display: 'block', fontStyle: 'italic' }}
      >
        {note}
      </Typography>
    )}
  </Box>
);

const BriefPanel: React.FC<{
  nomination: string;
  brief: string;
  scores?: BriefTaskScores;
}> = ({ nomination, brief, scores }) => {
  const { t } = useTranslation();
  const v3 = scores?.validity !== undefined;
  const excluded = v3 && scores?.validity !== 'on_task';

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {scores?.error && <Alert severity="error">{scores.error}</Alert>}
      {excluded && <Alert severity="warning">{t('rmet.validity_excluded_hint')}</Alert>}

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '2fr 1fr' }} gap={2}>
        <Field label={t('rmet.detail_nomination')} value={nomination || '—'} />
        <Field
          label={t('rmet.detail_team_type')}
          value={
            scores ? (
              <Tooltip title={scores.notes?.team_type ?? ''} placement="top">
                <span>{t(`rmet.team_type_${scores.teamType ?? 'uncoded'}`)}</span>
              </Tooltip>
            ) : (
              '—'
            )
          }
        />
      </Box>

      {scores && v3 && (
        <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }} gap={2}>
          <Field
            label={t('rmet.detail_validity')}
            value={
              <Tooltip title={scores.notes?.validity ?? ''} placement="top">
                <span>{t(`rmet.validity_${scores.validity ?? 'uncoded'}`)}</span>
              </Tooltip>
            }
          />
          <Field
            label={t('rmet.detail_individual_monitoring')}
            value={
              <Tooltip title={scores.notes?.individual_monitoring ?? ''} placement="top">
                <span>
                  {t(`rmet.individual_monitoring_${scores.individualMonitoring ? 'yes' : 'no'}`)}
                </span>
              </Tooltip>
            }
          />
          <Field
            label={t('rmet.detail_strict_total')}
            value={`${scores.strictTotal ?? 0} / ${BRIEF_NODE_COUNT}`}
          />
          <Field label={t('rmet.detail_word_count')} value={scores.wordCount ?? '—'} />
        </Box>
      )}

      <Box>
        <Typography variant="caption" color="text.secondary">
          {t('rmet.detail_brief')}
        </Typography>
        <TextBlock text={brief || '—'} muted={!brief} />
      </Box>

      {scores && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            {t('rmet.detail_nodes')}
          </Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            {BRIEF_NODE_GROUPS.map(({ group, keys }) => (
              <Box key={group}>
                <Typography variant="overline" color="text.secondary">
                  {t(`rmet.node_group_${group}`)} ·{' '}
                  {keys.reduce((sum, key) => sum + nodeLevel(scores.nodes?.[key]), 0)} /{' '}
                  {keys.length * 2}
                </Typography>
                {keys.map((key) => (
                  <NodeRow
                    key={key}
                    label={t(`rmet.node_${key}`)}
                    level={nodeLevel(scores.nodes?.[key])}
                    note={scores.notes?.[key]}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

interface Props {
  submission: RmetSubmission | null;
  onClose: () => void;
  onRescore: (id: string) => Promise<void>;
}

export const RmetSubmissionDialog: React.FC<Props> = ({ submission, onClose, onRescore }) => {
  const { t } = useTranslation();
  const [rescoring, setRescoring] = React.useState(false);

  if (!submission) return null;
  const s = submission;
  const scores = s.promptScores;
  const status = s.promptScoreStatus ?? 'pending';
  const legacy = s.promptTask !== BRIEF_TASK;
  const timings = s.timings;

  const demographicLabel = (field: string, value: string): string =>
    value ? t(`rmet.${field}_${value}`, { defaultValue: value }) : '—';

  const handleRescore = async () => {
    setRescoring(true);
    try {
      await onRescore(s.id);
    } finally {
      setRescoring(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <span>{new Date(s.submittedAt).toLocaleString()}</span>
          <Chip size="small" label={`RMET ${s.score} / ${RMET_TOTAL}`} />
          {scores && <Chip size="small" color="primary" label={promptScoreLabel(scores, t)} />}
          <Chip
            size="small"
            variant="outlined"
            color={status === 'done' ? 'success' : status === 'failed' ? 'error' : 'default'}
            label={t(`rmet.detail_status_${status}`)}
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {status === 'pending' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('rmet.detail_pending_hint')}
          </Alert>
        )}
        {legacy && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('rmet.detail_legacy_hint')}
          </Alert>
        )}
        {status === 'failed' && !legacy && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('rmet.detail_failed_hint')}
          </Alert>
        )}

        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }}
          gap={2}
          mb={2}
        >
          <Field label={t('rmet.age')} value={s.demographics.age || '—'} />
          <Field
            label={t('rmet.gender')}
            value={demographicLabel('gender', s.demographics.gender)}
          />
          <Field
            label={t('rmet.education')}
            value={demographicLabel('education', s.demographics.education)}
          />
          <Field
            label={t('rmet.degree_field')}
            value={demographicLabel('degree_field', s.demographics.degreeField)}
          />
          <Field
            label={t('rmet.native_language')}
            value={demographicLabel('native_language', s.demographics.nativeLanguage)}
          />
          <Field label={t('nav.language')} value={s.lang.toUpperCase()} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }}
          gap={2}
          mb={2}
        >
          <Field
            label={t('rmet.detail_rmet_duration')}
            value={durationBetween(timings?.rmetStartedAt, timings?.rmetEndedAt) ?? '—'}
          />
          {/* The nomination/brief split only exists on runs from the two-screen
              flow; single-screen runs have no boundary between them. */}
          {legacy || !timings?.taskStartedAt ? (
            <Field
              label={t('rmet.detail_prompts_duration')}
              value={durationBetween(timings?.promptsStartedAt, timings?.promptsEndedAt) ?? '—'}
            />
          ) : (
            <>
              <Field
                label={t('rmet.detail_nomination_duration')}
                value={durationBetween(timings?.promptsStartedAt, timings?.taskStartedAt) ?? '—'}
              />
              <Field
                label={t('rmet.detail_brief_duration')}
                value={durationBetween(timings?.taskStartedAt, timings?.promptsEndedAt) ?? '—'}
              />
            </>
          )}
          <Field
            label={t('rmet.detail_total_duration')}
            value={durationBetween(timings?.rmetStartedAt, timings?.promptsEndedAt) ?? '—'}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {legacy ? (
          <Box display="flex" flexDirection="column" gap={3}>
            {[1, 2, 3].map((n) => (
              <TaskPanel
                key={n}
                n={n}
                task={
                  scores && isLegacyScores(scores)
                    ? scores[`task${n}` as 'task1' | 'task2' | 'task3']
                    : undefined
                }
                promptFallback={s.prompts[n - 1] ?? ''}
              />
            ))}
          </Box>
        ) : (
          <BriefPanel
            nomination={s.nomination ?? ''}
            brief={s.prompts[0] ?? ''}
            scores={scores && !isLegacyScores(scores) ? scores : undefined}
          />
        )}

        {scores && (
          <Typography variant="caption" color="text.secondary" display="block" mt={2}>
            {t('rmet.detail_scored_at', {
              at: new Date(scores.scoredAt).toLocaleString(),
              version: scores.scorerVersion,
            })}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleRescore}
          loading={rescoring}
          disabled={legacy || status === 'pending'}
        >
          {t('rmet.detail_rescore')}
        </Button>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
